import posthog from 'posthog-js';

// Environment variables
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com';

// Cookie consent key (matches CookieConsent component)
const COOKIE_CONSENT_KEY = 'cookie_consent';

/**
 * Check if analytics consent has been given
 */
export function hasAnalyticsConsent(): boolean {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Initialize PostHog if consent is given and key is configured
 * Call this once on app startup
 */
export function initPostHog(): void {
  // Skip if no API key configured
  if (!POSTHOG_KEY || POSTHOG_KEY === 'phc_your_project_api_key') {
    console.log('[PostHog] Skipped: No API key configured');
    return;
  }

  const hasConsent = hasAnalyticsConsent();

  // Initialize PostHog (but don't start capturing yet)
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Don't capture until we check consent
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Disable persistence until consent
    persistence: hasConsent ? 'localStorage+cookie' : 'memory',

    // Session Replay configuration
    disable_session_recording: !hasConsent, // Only record with consent
    session_recording: {
      // Capture console logs for debugging
      consoleLogRecordingEnabled: true,
      // Privacy settings - mask sensitive data
      maskAllInputs: true, // Mask all input fields by default
      maskTextSelector: '[data-ph-mask]', // Custom selector for additional masking
      // Network request capture for debugging API issues
      recordCrossOriginIframes: false,
    },

    // Load feature flags (always allowed, no personal data)
    bootstrap: {
      featureFlags: {},
    },
  });

  // Check consent and opt in/out accordingly
  if (hasAnalyticsConsent()) {
    posthog.opt_in_capturing();
    console.log('[PostHog] Initialized with analytics consent');
  } else {
    posthog.opt_out_capturing();
    console.log('[PostHog] Initialized without consent (opted out)');
  }
}

/**
 * Update PostHog consent state
 * Call this when user updates cookie preferences
 */
export function updatePostHogConsent(analyticsEnabled: boolean): void {
  if (!posthog.__loaded) return;

  if (analyticsEnabled) {
    posthog.opt_in_capturing();
    // Switch to persistent storage and enable session recording
    posthog.set_config({
      persistence: 'localStorage+cookie',
      disable_session_recording: false,
    });
    // Start session recording
    posthog.startSessionRecording();
    console.log('[PostHog] User opted in to analytics + session recording');
  } else {
    // Stop session recording first
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    // Clear any stored data
    posthog.reset();
    console.log('[PostHog] User opted out of analytics');
  }
}

/**
 * Identify the current user
 * Call this on login
 */
export function identifyUser(user: {
  id: string;
  email: string;
  fullName?: string;
  role?: string;
  companyId?: string;
  isSuperAdmin?: boolean;
}): void {
  if (!posthog.__loaded || !hasAnalyticsConsent()) return;

  // Exclude superadmins from all tracking
  if (user.isSuperAdmin) {
    posthog.opt_out_capturing();
    console.log('[PostHog] Superadmin excluded from tracking');
    return;
  }

  posthog.identify(user.id, {
    email: user.email,
    name: user.fullName,
    role: user.role,
    company_id: user.companyId,
  });
  console.log('[PostHog] User identified:', user.id);
}

/**
 * Reset user identity
 * Call this on logout
 */
export function resetUser(): void {
  if (!posthog.__loaded) return;
  posthog.reset();
  console.log('[PostHog] User reset');
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!posthog.__loaded || !hasAnalyticsConsent()) return;
  posthog.capture(eventName, properties);
}

/**
 * Track a page view
 */
export function trackPageView(path?: string): void {
  if (!posthog.__loaded || !hasAnalyticsConsent()) return;
  posthog.capture('$pageview', {
    $current_url: path || window.location.href,
  });
}

/**
 * Check if a feature flag is enabled
 * Feature flags work even without analytics consent
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (!posthog.__loaded) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Get a feature flag value (for multivariate flags)
 */
export function getFeatureFlagValue(flagKey: string): string | boolean | undefined {
  if (!posthog.__loaded) return undefined;
  return posthog.getFeatureFlag(flagKey);
}

/**
 * Reload feature flags from server
 */
export function reloadFeatureFlags(): void {
  if (!posthog.__loaded) return;
  posthog.reloadFeatureFlags();
}

// Re-export posthog instance for advanced usage
export { posthog };

// Expose to window for debugging (development only)
if (typeof window !== 'undefined') {
  (window as unknown as { posthog: typeof posthog }).posthog = posthog;
}
